import pdfiumWrapper from '@src/modules/pdfium_wrapper';
import type React from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';

// スケルトン表示の閾値（ms）- この時間以内に完了する場合はスケルトンを表示しない
const SKELETON_DELAY_THRESHOLD = 100;

/**
 * PDFレンダリングロジックを管理するカスタムフック
 * PDFium WASM（高速）を使用してPDFをレンダリング
 *
 * @param canvasRef - キャンバス要素への参照
 * @param pdfUrl - PDFのURL
 * @param page - 現在のページ番号
 * @param isFocusMode - フォーカスモード状態
 * @param onPageTotalChange - 総ページ数変更時のコールバック
 * @returns レンダリング状態、エラー状態、キャンバスキー
 */
export const usePdfRendering = (
  canvasRef: React.RefObject<HTMLCanvasElement | null>,
  pdfUrl: string,
  page: number,
  isFocusMode: boolean,
  onPageTotalChange?: (total: number) => void
) => {
  const [pageTotal, setPageTotal] = useState<number>(1);
  // スケルトン表示用の状態（遅延表示用）
  const [showSkeleton, setShowSkeleton] = useState<boolean>(false);
  // 実際のレンダリング状態
  const [isActuallyRendering, setIsActuallyRendering] = useState<boolean>(true);
  const [pdfError, setPdfError] = useState<{ type: string; message: string } | null>(null);

  const renderTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const skeletonTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const lastRenderParamsRef = useRef<{ url: string; page: number } | null>(null);
  const prevFocusModeRef = useRef<boolean>(isFocusMode);

  /**
   * キャンバスをリセットして再レンダリングをトリガー
   */
  const resetCanvas = useCallback(() => {
    lastRenderParamsRef.current = null;
    setIsActuallyRendering(false);
    setShowSkeleton(false);
    if (skeletonTimeoutRef.current) {
      clearTimeout(skeletonTimeoutRef.current);
      skeletonTimeoutRef.current = null;
    }
    setPdfError(null);
  }, []);

  /**
   * ページ総数の更新処理
   */
  const handlePageTotalUpdate = useCallback(
    (total: number, error?: { type: string; message: string }) => {
      if (total > 0) {
        setPageTotal(total);
        if (onPageTotalChange) {
          onPageTotalChange(total);
        }
        setPdfError(null);
      } else if (total === -1) {
        setPageTotal(0);
        if (error) {
          setPdfError(error);
        } else {
          setPdfError({
            type: 'unknown_error',
            message: 'PDFファイルの処理中にエラーが発生しました',
          });
        }
      } else {
        setPageTotal(1);
        setPdfError(null);
      }
      // レンダリング完了時にスケルトンも非表示に
      setIsActuallyRendering(false);
      setShowSkeleton(false);
      if (skeletonTimeoutRef.current) {
        clearTimeout(skeletonTimeoutRef.current);
        skeletonTimeoutRef.current = null;
      }
    },
    [onPageTotalChange]
  );

  /**
   * PDFレンダリングの実行
   * PDFium WASMを使用してPDFをCanvasにレンダリング
   */
  const executeRender = useCallback(
    (currentParams: { url: string; page: number }, isUrlChange: boolean) => {
      if (!canvasRef.current || !currentParams.url) {
        return;
      }

      // 実際のレンダリング開始
      setIsActuallyRendering(true);
      lastRenderParamsRef.current = currentParams;

      // スケルトン表示を遅延させる - 閾値以内に完了すればスケルトンは表示されない
      if (skeletonTimeoutRef.current) {
        clearTimeout(skeletonTimeoutRef.current);
      }
      skeletonTimeoutRef.current = setTimeout(() => {
        // まだレンダリング中ならスケルトンを表示
        setShowSkeleton(true);
      }, SKELETON_DELAY_THRESHOLD);

      const doRender = async () => {
        if (!canvasRef.current) {
          return;
        }

        const canvasRefTyped = canvasRef as React.MutableRefObject<HTMLCanvasElement>;

        try {
          await pdfiumWrapper(
            canvasRefTyped,
            currentParams.url,
            currentParams.page,
            handlePageTotalUpdate
          );
        } catch (_error: unknown) {
          setIsActuallyRendering(false);
          setShowSkeleton(false);
          if (skeletonTimeoutRef.current) {
            clearTimeout(skeletonTimeoutRef.current);
            skeletonTimeoutRef.current = null;
          }
          setPdfError({
            type: 'render_error',
            message: 'PDFのレンダリング中にエラーが発生しました',
          });
        }
      };

      if (isUrlChange) {
        requestAnimationFrame(() => {
          doRender();
        });
      } else {
        doRender();
      }
    },
    [canvasRef, handlePageTotalUpdate]
  );

  /**
   * PDFの描画処理
   */
  useEffect(() => {
    if (renderTimeoutRef.current) {
      clearTimeout(renderTimeoutRef.current);
    }

    if (isActuallyRendering) {
      return;
    }

    const currentParams = { url: pdfUrl, page };
    const lastParams = lastRenderParamsRef.current;

    // 同じパラメータの場合はスキップ
    if (
      lastParams &&
      lastParams.url === currentParams.url &&
      lastParams.page === currentParams.page
    ) {
      return;
    }

    const isUrlChange = !lastParams || lastParams.url !== currentParams.url;
    const isFocusModeChange = prevFocusModeRef.current !== isFocusMode;
    // 遅延を最小限に抑えてスケルトン表示時間を短縮（以前は300ms）
    const delay = isUrlChange || isFocusModeChange ? 50 : 0;

    renderTimeoutRef.current = setTimeout(() => {
      executeRender(currentParams, isUrlChange);
    }, delay);

    prevFocusModeRef.current = isFocusMode;
  }, [pdfUrl, page, isActuallyRendering, isFocusMode, executeRender]);

  // クリーンアップ
  useEffect(() => {
    return () => {
      if (renderTimeoutRef.current) {
        clearTimeout(renderTimeoutRef.current);
      }
      if (skeletonTimeoutRef.current) {
        clearTimeout(skeletonTimeoutRef.current);
      }
    };
  }, []);

  // 外部からはshowSkeletonをisRenderingとして返す（スケルトン表示制御用）
  // isActuallyRenderingは内部のレンダリング状態追跡用
  return {
    pageTotal,
    isRendering: showSkeleton,
    pdfError,
    lastRenderParamsRef,
    resetCanvas,
    setIsRendering: setIsActuallyRendering,
    setPageTotal,
    setPdfError,
  };
};
